import { contactPrice } from '../contacts/perks';
import { playerLockupPoi } from './playerLockup';
import type { GameContext } from '../core/context';
import { forward, type V2 } from '../core/math';
import type { Rng } from '../core/rng';
import type { System } from '../core/system';
import { Layer } from '../physics/types';
import type { Vehicle } from '../vehicles/types';
import { getSpec } from '../vehicles/specs';
import type { PointOfInterest } from '../world/types';
import {
  LOCKUP_CAR_SPEC,
  LOCKUP_PRICE,
  LOCKUP_UNLOCK_ID,
  PLAYER_MAX_HEALTH,
  PlayerHealth,
  Wallet,
  type MoneyAuthority,
  bustedFine,
  formatMoney,
  hospitalBill,
  repairCost,
  resprayCost,
} from './wallet';
import { STARTING_MONEY } from '../save/saveData';

/**
 * Economy: the wallet, rewards, penalties and the small spend loop.
 *
 *  - Pays `reward` on `missionCompleted` (missions must NOT pay it themselves — put bonuses in the reward).
 *  - Spend loop: pay-and-spray (clears heat, repairs, new paint, car no longer recognised), garage
 *    patch-ups, and the lockup key (one-off unlock that gives you a free getaway car).
 *  - Consequences: `playerBusted` / `playerWasted` (emitted by the police module, which owns player health
 *    and the respawn) cost the penalty the police computed; economy applies it, updates stats and raises
 *    `playerOutcome` for the UI. Without a police module (tests, debug hooks) economy respawns itself.
 */
export interface EconomyService {
  readonly balance: number;
  readonly health: number;
  readonly maxHealth: number;
  canAfford(cost: number): boolean;
  /** Add money (rewards, loot). */
  earn(amount: number, reason: string): number;
  /** Pay for something; false (and a refusal toast is the caller's job) when short. */
  spend(cost: number, reason: string): boolean;
  /** Fine: takes up to `amount`, never below zero. */
  fine(amount: number, reason: string): number;
  /** Teleport to the respawn POI, clear heat, restore health. Called by the UI after the outcome screen. */
  respawn(kind: 'busted' | 'wasted'): void;
  readonly outcomePending: 'busted' | 'wasted' | null;
  /** 'server' while playing online: local earn/spend/fine are refused (see Wallet). */
  readonly authority: MoneyAuthority;
  /**
   * Online gate (called only by src/net): 'server' freezes local money changes; 'local' hands control
   * back and restores the single-player balance from the save.
   */
  setAuthority(a: MoneyAuthority): void;
  /** Online: show the balance the server reported. */
  applyServerBalance(balance: number, reason: string): void;
  /**
   * The respray's consequences, already paid for (Pike's spray bay, src/garage, sells the paint): repaired,
   * new plates (no longer stolen, now yours), heat off, `resprayed`. The colour is the caller's.
   */
  applyRespray(v: Vehicle, cost: number): void;
  /** This car is now your car (Pike kept it): it is the one Lockup 7 holds from now on. */
  adoptLockupCar(v: Vehicle): void;
}

declare module '../core/services' {
  interface ServiceMap {
    economy: EconomyService;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Give (or with a negative number, take) money. */
    giveMoney(amount: number): void;
    /** Trigger the NICKED consequence. */
    busted(): void;
    /** Trigger the WASTED consequence. */
    wasted(): void;
    economyState(): Record<string, unknown>;
  }
}

const SPRAY_SECONDS = 1.8;
const SPRAY_MAX_SPEED = 4;

/** A first arrest with less than this on you costs no fine. */
export const FIRST_OFFENCE_BELOW = 100;

export function createEconomySystem(): System {
  let ctx: GameContext;
  let wallet: Wallet;
  let layoutPois: PointOfInterest[] = [];
  const health = new PlayerHealth();
  const offs: (() => void)[] = [];
  let outcome: 'busted' | 'wasted' | null = null;
  let spray: { vehicle: Vehicle; t: number; cost: number } | null = null;
  let lockupCarId = -1;
  let rng: Rng;

  const ui = () => ctx.services.tryGet('ui');
  const toast = (text: string, kind: 'info' | 'good' | 'bad' | 'cash' | 'warn' = 'info', seconds?: number): void => ui()?.toast(text, kind, seconds);
  const save = () => ctx.services.get('save');

  const service: EconomyService = {
    get balance() {
      return wallet.balance;
    },
    get health() {
      return ctx.services.tryGet('police')?.health ?? health.value;
    },
    maxHealth: PLAYER_MAX_HEALTH,
    canAfford: (c) => {
      syncFromState();
      return wallet.canAfford(c);
    },
    earn: (a, r) => {
      syncFromState();
      return wallet.earn(a, r);
    },
    spend: (c, r) => {
      syncFromState();
      return wallet.spend(c, r);
    },
    fine: (a, r) => {
      syncFromState();
      return wallet.take(a, r);
    },
    respawn,
    get outcomePending() {
      return outcome;
    },
    get authority() {
      return wallet.authority;
    },
    setAuthority(a) {
      if (a === wallet.authority) return;
      wallet.setAuthority(a);
      if (a === 'local') wallet.reset(save().data.money, 'offline');
    },
    applyServerBalance(balance, reason) {
      wallet.applyAuthoritative(balance, reason);
    },
    adoptLockupCar(v) {
      const vehicles = ctx.services.get('vehicles');
      if (lockupCarId >= 0 && lockupCarId !== v.id) vehicles.get(lockupCarId)?.tags.delete('lockup:car');
      lockupCarId = v.id;
      v.tags.add('lockup:car');
      v.ownedByPlayer = true;
    },
    applyRespray(v, cost) {
      if (v.destroyed) return;
      ctx.services.get('vehicles').repair(v);
      v.stolen = false;
      v.ownedByPlayer = true;
      ctx.hooks.call('setHeat', 0);
      ctx.events.emit('resprayed', { vehicleId: v.id, cost });
    },
  };

  // ---------------------------------------------------------------- consequences

  /** True while the police module is running its own busted/wasted sequence (it respawns the player). */
  const policeHandling = (): boolean => (ctx.services.tryGet('police')?.outcome ?? 'none') !== 'none';

  /** `state.money` is public; if someone wrote it directly (debug, tests), adopt it as the balance. */
  function syncFromState(): void {
    if (ctx.state.money === wallet.balance) return;
    // Online the server's figure wins: undo direct writes instead of adopting them.
    if (wallet.authority !== 'local') ctx.state.money = wallet.balance;
    else wallet.reset(ctx.state.money, 'external');
  }

  function startOutcome(kind: 'busted' | 'wasted', penalty?: number): void {
    if (outcome) return;
    syncFromState();
    outcome = kind;
    spray = null;
    const player = ctx.services.get('player');
    const byPolice = policeHandling();
    if (!byPolice) player.controls.acquire('outcome');
    const stats = save().data.stats;
    let fine: number;
    let vehicleLost = false;
    let waived = false;
    if (kind === 'busted') {
      // A first offence with next to nothing on you: the sergeant lets the fine go. The first job's chase used
      // to end with the forty quid you start with gone, and £0 until a job paid (docs/FIRST-15-MINUTES.md #5).
      waived = stats.timesBusted === 0 && wallet.balance < FIRST_OFFENCE_BELOW;
      stats.timesBusted++;
      fine = waived ? 0 : wallet.take(penalty ?? bustedFine(wallet.balance), 'Nicked: fine and legal fees');
      const v = player.vehicle;
      // Stolen wheels get impounded; a car you own (bought / lockup) stays yours.
      if (v && !v.ownedByPlayer && !byPolice) vehicleLost = true;
    } else {
      stats.timesWasted++;
      fine = wallet.take(penalty ?? hospitalBill(wallet.balance), 'Wasted: hospital bill');
    }
    save().markDirty();
    ctx.events.emit('playerOutcome', { kind, fine, vehicleLost, ...(waived ? { waived } : {}) });
    // Headless / no UI to present the screen: respawn straight away (police respawns on its own timer).
    if (!byPolice && (!ctx.uiRoot || !ui())) respawn(kind);
  }

  function respawnPoint(kind: 'busted' | 'wasted'): { pos: V2; heading: number } {
    const layout = ctx.services.get('layout');
    const wanted = kind === 'busted' ? 'policeStation' : 'safehouse';
    const poi = layoutPois.find((p) => p.kind === wanted) ?? layoutPois.find((p) => p.kind === 'safehouse');
    if (!poi) return layout.playerSpawn;
    // Step a couple of metres off the door so we are not inside the building footprint.
    const f = forward(poi.heading);
    return { pos: { x: poi.pos.x + f.x * 2.2, z: poi.pos.z + f.z * 2.2 }, heading: poi.heading };
  }

  function respawn(kind: 'busted' | 'wasted'): void {
    if (outcome === null || policeHandling()) return;
    const player = ctx.services.get('player');
    const vehicles = ctx.services.get('vehicles');
    const v = player.vehicle;
    if (v) {
      player.exitVehicle();
      if (kind === 'busted' && !v.ownedByPlayer) vehicles.despawn(v);
    }
    const at = respawnPoint(kind);
    player.teleport(at.pos, at.heading);
    ctx.hooks.call('setHeat', 0);
    health.reset();
    outcome = null;
    player.controls.release('outcome');
    ctx.events.emit('playerRespawned', { reason: kind, pos: { x: at.pos.x, z: at.pos.z } });
  }

  // ---------------------------------------------------------------- shops

  function registerShops(): void {
    const interactions = ctx.services.get('interactions');
    const player = ctx.services.get('player');
    const driven = (): Vehicle | null => (player.mode === 'driving' ? player.vehicle : null);

    for (const poi of layoutPois) {
      if (poi.kind === 'resprayShop') {
        offs.push(
          interactions.register({
            id: `shop:${poi.id}`,
            pos: () => poi.pos,
            radius: Math.max(poi.radius, 4),
            priority: 3,
            whileDriving: true,
            enabled: () => {
              const v = driven();
              return wallet.authority === 'local' && !!v && !v.isPolice && !spray && !outcome && !ctx.services.tryGet('sprayBay')?.isOpen;
            },
            prompt: () => {
              const v = driven()!;
              const cost = contactPrice(ctx, 'respray', resprayCost(ctx.state.heat));
              const bay = ctx.services.tryGet('sprayBay');
              if (bay) return bay.prompt(poi.id);
              return wallet.canAfford(cost)
                ? `Respray the ${v.spec.displayName} — £${formatMoney(cost)} · new colour, new plates, heat off`
                : `Respray — £${formatMoney(cost)} (you're short)`;
            },
            // The drive-in bay (src/garage) sells the paint, wheels and the rest; without it, the quick spray.
            onInteract: () => {
              const bay = ctx.services.tryGet('sprayBay');
              if (bay) bay.open(poi.id);
              else beginSpray(poi);
            },
          }),
        );
      } else if (poi.kind === 'garage') {
        offs.push(
          interactions.register({
            id: `shop:${poi.id}`,
            pos: () => poi.pos,
            radius: Math.max(poi.radius, 4),
            priority: 4,
            whileDriving: true,
            enabled: () => {
              const v = driven();
              return wallet.authority === 'local' && !!v && !v.isPolice && repairCost(v.health, v.spec.maxHealth) > 0 && !spray && !outcome;
            },
            prompt: () => {
              const v = driven()!;
              const cost = repairCost(v.health, v.spec.maxHealth);
              return wallet.canAfford(cost) ? `Patch up the ${v.spec.displayName} — £${formatMoney(cost)}` : `Patch-up — £${formatMoney(cost)} (you're short)`;
            },
            onInteract: () => {
              const v = driven();
              if (!v) return;
              const cost = repairCost(v.health, v.spec.maxHealth);
              if (!wallet.spend(cost, 'garage')) return deny();
              ctx.services.get('vehicles').repair(v);
              toast(`${poi.name}: "Good as new, guv." −£${formatMoney(cost)}`, 'good');
            },
          }),
        );
      }
    }

    const lockup = playerLockup();
    if (lockup) {
      offs.push(
        interactions.register({
          id: `shop:${lockup.id}`,
          pos: () => lockup.pos,
          radius: Math.max(lockup.radius, 3.5),
          priority: 3,
          // Shut while a job is running: mission stashes share lockup rows and E must mean "get in".
          enabled: () => wallet.authority === 'local' && !outcome && ctx.state.activeMissionId === null && (!save().hasUnlock(LOCKUP_UNLOCK_ID) || !lockupCarAlive()),
          prompt: () => {
            if (save().hasUnlock(LOCKUP_UNLOCK_ID)) return 'Roll the shutter up — your car is inside';
            return wallet.canAfford(LOCKUP_PRICE)
              ? `Take the lockup key — £${formatMoney(LOCKUP_PRICE)} · comes with a getaway car`
              : `The lockup — £${formatMoney(LOCKUP_PRICE)} (you're short)`;
          },
          onInteract: () => {
            if (!save().hasUnlock(LOCKUP_UNLOCK_ID)) {
              if (!wallet.spend(LOCKUP_PRICE, 'lockup')) return deny();
              save().addUnlock(LOCKUP_UNLOCK_ID);
              ctx.events.emit('unlockPurchased', { id: LOCKUP_UNLOCK_ID, cost: LOCKUP_PRICE });
              toast(`${lockup.name} is yours. Keys on the hook, motor inside.`, 'good');
            }
            spawnLockupCar(lockup);
          },
        }),
      );
    }
  }

  /** The player's own lockup: the one on Pilcher Ginnel by the safehouse, else the first in the layout. */
  function playerLockup(): PointOfInterest | undefined {
    return playerLockupPoi(layoutPois);
  }

  function deny(): void {
    toast(wallet.authority === 'local' ? 'Not enough cash.' : 'Not online yet — this one deals in cash only offline.', 'bad');
  }

  function lockupCarAlive(): boolean {
    if (lockupCarId < 0) return false;
    const v = ctx.services.get('vehicles').get(lockupCarId);
    return !!v && !v.destroyed;
  }

  function spawnLockupCar(lockup: PointOfInterest): void {
    if (lockupCarAlive()) return;
    const vehicles = ctx.services.get('vehicles');
    // Your own car (the motor in the save: Halloran's or Pike's work) once you have one; else the stock Vesper.
    let spec = ctx.services.tryGet('garage')?.motorSpec() ?? LOCKUP_CAR_SPEC;
    try {
      getSpec(spec);
    } catch {
      spec = LOCKUP_CAR_SPEC;
    }
    const f = forward(lockup.heading);
    const side = { x: -f.z, z: f.x };
    const heading = lockup.heading;
    const half = { type: 'box' as const, hx: 1.0, hz: 2.4 };
    let at: V2 | null = null;
    for (const k of [3.2, 4.4, 5.8, 7.5]) {
      for (const s of [0, 1.8, -1.8]) {
        const p = { x: lockup.pos.x + f.x * k + side.x * s, z: lockup.pos.z + f.z * k + side.z * s };
        if (ctx.physics.isShapeFree(half, p, heading, Layer.ALL)) {
          at = p;
          break;
        }
      }
      if (at) break;
    }
    at ??= { x: lockup.pos.x + f.x * 4, z: lockup.pos.z + f.z * 4 };
    const v = vehicles.spawn(spec, at, { heading, ownedByPlayer: true, tags: ['lockup:car'], paint: 0x2b3b5c });
    lockupCarId = v.id;
    // Your motor from Halloran's workshop (src/activities): when it is a Vesper too, the lockup car wears its build.
    ctx.services.tryGet('garage')?.dress(v);
  }

  // ---------------------------------------------------------------- pay-and-spray

  function beginSpray(poi: PointOfInterest): void {
    const player = ctx.services.get('player');
    const v = player.mode === 'driving' ? player.vehicle : null;
    if (!v || spray) return;
    if (v.speed > SPRAY_MAX_SPEED) {
      toast('Pull up properly — the sprayer won\'t chase you.', 'warn');
      return;
    }
    const cost = contactPrice(ctx, 'respray', resprayCost(ctx.state.heat));
    if (!wallet.spend(cost, 'respray')) return deny();
    spray = { vehicle: v, t: 0, cost };
    player.controls.acquire('respray');
    toast(`${poi.name}: "Shutter's down. Don't look, don't ask."`, 'info', 2.4);
  }

  function finishSpray(): void {
    const s = spray;
    spray = null;
    ctx.services.get('player').controls.drop('respray');
    if (!s) return;
    const v = s.vehicle;
    if (v.destroyed) return;
    ctx.services.get('vehicles').repair(v);
    const choices = v.spec.paints.filter((p) => p !== v.paint);
    if (choices.length) v.paint = choices[Math.floor(rng.next() * choices.length)]!;
    // New plates: the cops stop recognising it and the car becomes yours to take without a crime.
    v.stolen = false;
    v.ownedByPlayer = true;
    ctx.hooks.call('setHeat', 0);
    ctx.events.emit('resprayed', { vehicleId: v.id, cost: s.cost });
    toast('Fresh coat, fresh plates. The heat is off.', 'good', 3.2);
  }

  // ---------------------------------------------------------------- system

  return {
    id: 'economy',
    order: 510,
    init(c) {
      ctx = c;
      rng = c.rng.fork('economy');
      const sv = c.services.get('save');
      wallet = new Wallet(sv.data.money, (amount, delta, reason) => {
        c.state.money = amount;
        c.events.emit('moneyChanged', { amount, delta, reason });
      });
      c.state.money = wallet.balance;
      c.services.provide('economy', service);

      offs.push(
        c.events.on('missionCompleted', (e) => {
          syncFromState();
          wallet.earn(e.reward, `mission:${e.id}`);
        }),
        c.events.on('playerHurt', (e) => {
          // The police module owns player health when present; this is the standalone fallback.
          if (outcome || c.services.tryGet('police')) return;
          if (health.hurt(e.amount)) {
            const p = c.services.get('player').pos;
            c.events.emit('playerWasted', { pos: { x: p.x, z: p.z }, penalty: hospitalBill(wallet.balance), cause: e.cause });
          }
        }),
        c.events.on('playerBusted', (e) => startOutcome('busted', e.penalty)),
        c.events.on('playerWasted', (e) => startOutcome('wasted', e.penalty)),
        c.events.on('playerRespawned', () => {
          outcome = null;
          health.reset();
        }),
        c.events.on('newGameStarted', () => {
          wallet.reset(STARTING_MONEY, 'newGame');
          health.reset();
          outcome = null;
          if (lockupCarId >= 0) c.services.get('vehicles').despawn(lockupCarId);
          lockupCarId = -1;
        }),
      );

      c.hooks.register('giveMoney', (n) => {
        if (n >= 0) wallet.earn(n, 'debug');
        else wallet.take(-n, 'debug');
      });
      c.hooks.register('busted', () => {
        const p = c.services.get('player').pos;
        c.events.emit('playerBusted', { pos: { x: p.x, z: p.z }, penalty: bustedFine(wallet.balance) });
      });
      c.hooks.register('wasted', () => {
        // Prefer the police module's real sequence (health -> 0); fall back to the bare event.
        if (c.hooks.has('setPlayerHealth')) return c.hooks.call('setPlayerHealth', 0);
        const p = c.services.get('player').pos;
        c.events.emit('playerWasted', { pos: { x: p.x, z: p.z }, penalty: hospitalBill(wallet.balance), cause: 'debug' });
      });
      c.hooks.register('economyState', () => ({
        balance: wallet.balance,
        health: Math.round(service.health),
        outcome,
        spraying: spray !== null,
        lockupCarId,
        lockupUnlocked: sv.hasUnlock(LOCKUP_UNLOCK_ID),
      }));
      c.hooks.registerSnapshot('economy', () => ({ balance: wallet.balance, health: Math.round(service.health), outcome }));
    },
    start() {
      layoutPois = ctx.services.get('layout').pois;
      registerShops();
      const lockup = playerLockup();
      if (lockup && ctx.services.get('save').hasUnlock(LOCKUP_UNLOCK_ID)) spawnLockupCar(lockup);
    },
    fixedUpdate(dt) {
      syncFromState();
      health.update(dt);
      if (spray) {
        spray.t += dt;
        const v = spray.vehicle;
        if (v.destroyed || ctx.services.get('player').vehicle !== v) {
          spray = null;
          ctx.services.get('player').controls.drop('respray');
        } else if (spray.t >= SPRAY_SECONDS) finishSpray();
      }
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
    },
  };
}
